<template>
	<div class="route-optimizer-map">
		<div class="route-optimizer-map__title">{{ t('RouteRedrawnReadOnly') }}</div>
		<div v-if="routes.length > 1" class="route-optimizer-map__legend">
			<label v-for="route in routes" :key="route.date" class="route-optimizer-map__legend-item" :class="{ 'route-optimizer-map__legend-item--hidden': hiddenDates.has(route.date) }">
				<v-checkbox-btn :model-value="!hiddenDates.has(route.date)" :color="route.color" density="compact" @update:model-value="toggleRoute(route.date, $event)" />
				{{ route.label }}
			</label>
		</div>
		<div class="route-optimizer-map__map">
			<MapLibre ref="mapRef" :locations="locations" :polylines="polylines" location-id-key="id" location-title-key="label" :tool-box="[]" :bottom-tool-box="[]" :show-popup-on-click="false" :hover-highlight="false" :show-user-position="false" hide-route-summary />
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { routeService } from '@/services/routeService';
import { buildTimelineEvents } from '@/utils/domain/routeOptimizer';
import { log } from '@/utils/core/vueLogger.js';
import MapLibre from '@/components/map/MapLibre.vue';
import { CATEGORY_PALETTE } from '@/utils/ui/palette';

const FIXED_POINT_COLOR = '#696969';

const props = defineProps({
	days: { type: Array, default: () => [] },
	points: { type: Array, default: () => [] },
	mode: { type: String, default: 'after' },
});

const t = useTranslator();
const locale = iframeDataBridge.getCurrentUser()?.locale || 'fr-FR';
const mapRef = ref(null);
const geometries = ref({});
let requestId = 0;

const hasIndex = (index) => index !== null && index !== undefined && !!props.points[index];

const routes = computed(() =>
	props.days
		.filter((day) => !day.excluded)
		.map((day) => {
			const stops = buildTimelineEvents(day, props.mode)
				.filter((e) => e.kind === 'stop' && e.step.item.isStore && hasIndex(e.step.item.point))
				.map((e) => e.step);
			const indexes = [...(hasIndex(day.startPoint) ? [day.startPoint] : []), ...stops.map((s) => s.item.point), ...(hasIndex(day.endPoint) ? [day.endPoint] : [])];
			const waypoints = indexes.map((i) => [props.points[i].lng, props.points[i].lat]);
			const weekday = new Date(`${day.date}T12:00:00`);
			return { date: day.date, day, stops, waypoints, color: CATEGORY_PALETTE[(weekday.getDay() + 6) % 7], label: weekday.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' }) };
		})
		.filter((r) => r.stops.length)
);

const hiddenDates = ref(new Set());
const visibleRoutes = computed(() => routes.value.filter((route) => !hiddenDates.value.has(route.date)));

function toggleRoute(date, visible) {
	const next = new Set(hiddenDates.value);
	if (visible) next.delete(date);
	else next.add(date);
	hiddenDates.value = next;
}

const fixedMarker = (index, id) => {
	const point = props.points[index];
	return { id, label: point.label, lat: point.lat, lng: point.lng, icon: point.kind === 'night' ? 'hotel' : 'home', color: FIXED_POINT_COLOR, _fixed: true };
};

const locations = computed(() => {
	const markers = new Map();
	visibleRoutes.value.forEach((route) => {
		const { day } = route;
		if (hasIndex(day.startPoint)) markers.set(`point-${day.startPoint}`, fixedMarker(day.startPoint, `point-${day.startPoint}`));
		if (hasIndex(day.endPoint)) markers.set(`point-${day.endPoint}`, fixedMarker(day.endPoint, `point-${day.endPoint}`));
		route.stops.forEach((step, index) => {
			const point = props.points[step.item.point];
			markers.set(`${route.date}-${step.item.uid}`, { id: `${route.date}-${step.item.uid}`, label: step.item.label, lat: point.lat, lng: point.lng, sequence: index + 1, icon: step.kind === 'pinned' ? 'keep' : 'storefront', color: route.color });
		});
	});
	return [...markers.values()];
});

const polylines = computed(() =>
	visibleRoutes.value.map((route) => ({
		id: `route-${route.date}`,
		path: geometries.value[route.date] || route.waypoints,
		color: route.color,
		width: 5,
	}))
);

const geometryCache = new Map();

async function routeGeometry(route) {
	if (route.waypoints.length < 2) return route.waypoints;
	const key = route.waypoints.join(';');
	if (geometryCache.has(key)) return geometryCache.get(key);
	try {
		const coordinates = (await routeService.getRouteWithFallback(route.waypoints))?.features?.[0]?.geometry?.coordinates;
		if (!coordinates) return route.waypoints;
		geometryCache.set(key, coordinates);
		return coordinates;
	} catch (error) {
		log('warn', 'RouteOptimizer', `Route geometry failed for ${route.date}: ${error?.message || error}`);
		return route.waypoints;
	}
}

async function loadGeometries() {
	const current = ++requestId;
	geometries.value = {};
	const entries = await Promise.all(routes.value.map(async (route) => [route.date, await routeGeometry(route)]));
	if (current !== requestId) return;
	geometries.value = Object.fromEntries(entries);
	await nextTick();
	mapRef.value?.fitCoordinates?.(routes.value.flatMap((r) => r.waypoints));
}

watch(
	() => props.days,
	() => (hiddenDates.value = new Set())
);
watch(() => [props.days, props.mode, props.points], loadGeometries);
onMounted(loadGeometries);
</script>
